import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import FooterContent from '../Footer/FooterContent';
import MainNav from "../Navigation/MainNav";
import NavContent from "../Navigation/NavContent";
import Box from "@mui/material/Box";
import Container from '@mui/material/Container';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Layout styles.
import { LayoutPageStyles as styles } from "./LayoutPage.styles";
import Grid from "@mui/material/Grid";

/**
 * Return layout structure for Crocus pages
 * @param pageTitle {string, object} The title of the web page.
 * @param documentTitle {string} The document title displayed on the browser tab.
 * @param description {string} Informative description of the web page.
 * @param type {string} The web page type to be rendered.
 *  - basic: The default page with the base styles/structure Crocus page.
 *  - dashboard: Page with custom styles/structure for data dashboards.
 *  - crud: Page with a data grid component to add/update/delete data.
 * @param customClass {string} Classes for specific style customization.
 * @param children {element} Elements to build the page ui.
 * @param sidebar { element } Sidebar page elements.
 * @param topContent { element } Content to be displayed on top page.
 * @param container { boolean } Fixed/fluid values to set the max width of the layout.
 */
function LayoutPage({ pageTitle, documentTitle= null, description, type = 'basic', container = 'fixed', customClass, children, sidebar, topContent }) {

  const [isSticky, setIsSticky] = useState(false)
  const [topCollapsed, setTopCollapsed] = useState(false)

  useEffect(() => {
    // This will run when the page first loads.
    document.title = documentTitle ?? pageTitle
  }, [documentTitle, pageTitle]);

    // Adding effect and refs to control the sticky top content.
    const header = useRef();
    const topMenu = useRef();

    useEffect(() => {
      // Constants required to calculate the page height and avoid bug error on top content collapse.
      const main = document.querySelector('main');
      const viewportHeight = window?.innerHeight;
      const mainHeight = main.offsetHeight;
      const topContentHeight = document.querySelector('.Main__top-content')?.offsetHeight;
      if(topContentHeight) {
        if((mainHeight + topContentHeight) < viewportHeight) {
          // Min height to avoid bug on top content collapsing
          main.style.minHeight = `${viewportHeight + topContentHeight + 16}px`;
        }
        const observer = new IntersectionObserver(entries => {
          // Observing element position and body height to collapse Top content height avoiding blink height error.
          const isIntersecting = entries[0].intersectionRatio === 0;
          if(window.innerWidth < 768 && isIntersecting) {
            // Necessary to avoid blink when object collapses
            setTimeout(() => {
              setIsSticky(true);
              setTopCollapsed(true);
            }, 200)
          } else {
            setIsSticky(false);
          }
          // When resolution changes and is collapsed.
          if(!isIntersecting) {
            setTopCollapsed(false)
          }
        }, { threshold: [0, 1] });
        observer.observe(header.current);
      }
    }, []);

    return (
    <Box  id="LayoutPage" className={`${type}-page ${customClass} ${(sidebar && type === 'basic') ? 'with-sidebar' : 'full' }`} sx={styles}>
      <Header reference={header} pageTitle={pageTitle} />
      <MainNav>
        <NavContent/>
      </MainNav>
      <main>
        {
          topContent && (
            <div className="Main__top-content" ref={topMenu}>
              <>
                {
                  isSticky &&
                    <div className={`top-toggle ${!topCollapsed ? 'active' : ''}`}>
                      <button className={`bnt-top-toggle ${!topCollapsed ? 'active' : ''}`} onClick={()=> setTopCollapsed(!topCollapsed)}>
                        {
                          topCollapsed
                              ? <ExpandMoreIcon color="purple" sx={{ fontSize: 26 }} />
                              : <ExpandLessIcon color="purple" sx={{ fontSize: 26 }} />
                        }
                    </button>
                    </div>
                }
                <Container className={`${isSticky ? "sticky-menu" : "regular"}`} maxWidth='xl' fixed style={{display: `${!topCollapsed ? 'block' : 'none'}`}}>
                  { topContent }
                </Container>
              </>
            </div>
          )
        }
        <div className="Main__content">
          <Container maxWidth={ container === 'fixed' ? 'xl' : false } fixed={container === 'fixed'}>
            <Grid container spacing={0} columns={12}>
              {
                // Only basic page can display a sidebar content region
                (sidebar && type === 'basic') &&  (
                    <Grid item component="aside"  xs={12} md={4} className="sidebar">
                      { sidebar }
                    </Grid>
                )
              }
              <Grid item xs={12} md={(sidebar && type === 'basic') ? 8 : 12} className='content'>
                { children }
              </Grid>
            </Grid>
          </Container>
        </div>
      </main>
      <Footer>
        <FooterContent/>
      </Footer>
    </Box>
  )
}

LayoutPage.propTypes = {
  pageTitle: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string
  ]).isRequired,
  description: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['crud', 'basic', 'dashboard']).isRequired,
  container: PropTypes.oneOf(['fixed', 'fluid']),
  customClass: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array
  ]).isRequired,
  sidebar: PropTypes.element,
  topContent: PropTypes.element,
}

export default LayoutPage;
